/** RBI Form A: households and their members. */
import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FileText, Plus, UserPlus } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { openFile } from '../../lib/files';
import { Alert, Button, Card, EmptyState, Field, FilterBar, Modal, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, filterClass, inputClass } from '../../components/ui';
import { barangaySettings, CIVIL, relLabel, STATUS } from './types';

interface HouseholdRow { id: string; code: string; purok: string; houseNo: string; street: string; members: number; head: string | null; headId: string | null }
interface HouseholdDetail { id: string; code: string; purok: string; houseNo: string; street: string; hazardZone: string; active: boolean; importSource: string | null; members: { id: string; code: string; displayName: string; relationship: string; sex: string; birthDate: string; age: number; civilStatus: string; occupation: string; status: 'ACTIVE' | 'TRANSFERRED_OUT' | 'DECEASED' | 'MERGED' }[] }

function HouseholdDialog({ initial, onClose }: { initial?: HouseholdDetail; onClose: (id?: string) => void }): JSX.Element {
  const qc = useQueryClient();
  const settings = useQuery(barangaySettings);
  const [form, setForm] = useState({ purok: initial?.purok ?? '', houseNo: initial?.houseNo ?? '', street: initial?.street ?? '' });
  const save = useMutation({
    mutationFn: () => (initial ? api<{ id: string }>(`/households/${initial.id}`, { method: 'PATCH', body: form }) : api<{ id: string }>('/households', { method: 'POST', body: form })),
    onSuccess: (h) => { void qc.invalidateQueries({ queryKey: ['households'] }); void qc.invalidateQueries({ queryKey: ['household'] }); onClose(h.id); },
  });
  return (
    <Modal title={initial ? `Edit ${initial.code}` : 'New household'} description={initial ? 'An address change is recorded in every member’s history.' : 'The household number is given automatically. Add members from the household page.'} onClose={() => onClose()}>
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {save.error && <Alert tone="critical">{save.error.message}</Alert>}
        <Field label="Purok / sitio">
          <select className={inputClass} value={form.purok} onChange={(e) => setForm({ ...form, purok: e.target.value })}>
            <option value="">—</option>{settings.data?.puroks.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="House no."><input className={inputClass} value={form.houseNo} onChange={(e) => setForm({ ...form, houseNo: e.target.value })} /></Field>
          <div className="sm:col-span-2"><Field label="Street"><input className={inputClass} value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} /></Field></div>
        </div>
        <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={() => onClose()}>Cancel</Button><Button type="submit" busy={save.isPending}>Save</Button></div>
      </form>
    </Modal>
  );
}

export function HouseholdsPage(): JSX.Element {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState('');
  const [purok, setPurok] = useState('');
  const [page, setPage] = useState(1);
  const settings = useQuery(barangaySettings);
  const list = useQuery({ queryKey: ['households', q, purok, page], queryFn: () => api<{ data: HouseholdRow[]; total: number }>(`/households?pageSize=50&page=${page}&q=${encodeURIComponent(q)}&purok=${encodeURIComponent(purok)}`), placeholderData: keepPreviousData });
  const creating = params.get('new') === '1';
  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title="Households" description="RBI Form A. Each household has one head and its members." actions={can('residents.manage') && <Button onClick={() => setParams({ new: '1' })}><Plus aria-hidden="true" className="h-4 w-4" /> New household</Button>} />
      <FilterBar>
        <input className={`${filterClass} w-64`} placeholder="Household no., street or member" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Search households" />
        <select className={filterClass} value={purok} onChange={(e) => { setPurok(e.target.value); setPage(1); }} aria-label="Purok"><option value="">All puroks</option>{settings.data?.puroks.map((p) => <option key={p} value={p}>{p}</option>)}</select>
      </FilterBar>
      <Card>
        {list.isLoading ? <Skeleton className="h-64 w-full" /> : !list.data?.data.length ? <EmptyState title="No households" /> : (
          <>
            <TableWrap>
              <thead><tr><Th>Household</Th><Th>Head</Th><Th>Address</Th><Th>Members</Th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {list.data.data.map((h) => (
                  <tr key={h.id}>
                    <Td><Link to={`/households/${h.id}`} className="font-mono text-sm font-medium hover:underline">{h.code}</Link></Td>
                    <Td>{h.headId ? <Link className="hover:underline" to={`/residents/${h.headId}`}>{h.head}</Link> : <span className="text-slate-400">No head yet</span>}</Td>
                    <Td><span className="text-sm">{[h.houseNo, h.street, h.purok].filter(Boolean).join(', ') || '—'}</span></Td>
                    <Td className="tabular-nums">{h.members}</Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
            <div className="mt-3 flex items-center justify-between text-sm text-slate-500">
              <span>{list.data.total} households</span>
              <div className="flex gap-2"><Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button><Button size="sm" variant="secondary" disabled={page * 50 >= list.data.total} onClick={() => setPage(page + 1)}>Next</Button></div>
            </div>
          </>
        )}
      </Card>
      {creating && <HouseholdDialog onClose={(id) => { setParams({}); if (id) navigate(`/households/${id}`); }} />}
    </div>
  );
}

export function HouseholdDetailPage(): JSX.Element {
  const { id } = useParams();
  const { can } = useAuth();
  const [editing, setEditing] = useState(false);
  const h = useQuery({ queryKey: ['household', id], queryFn: () => api<HouseholdDetail>(`/households/${id}`) });
  if (h.isLoading) return <Skeleton className="h-96 w-full" />;
  if (!h.data) return <Alert tone="critical">Household not found.</Alert>;
  const d = h.data;
  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title={`Household ${d.code}`}
        description={[d.houseNo, d.street, d.purok].filter(Boolean).join(', ') || 'No address recorded'}
        actions={<>
          <Button variant="secondary" onClick={() => void openFile(`/households/${d.id}/form-a.pdf`).catch((e: Error) => window.alert(e.message))}><FileText aria-hidden="true" className="h-4 w-4" /> RBI Form A</Button>
          {can('residents.manage') && <Button variant="secondary" onClick={() => setEditing(true)}>Edit address</Button>}
          {can('residents.manage') && <Link to={`/residents/new?household=${d.id}`}><Button><UserPlus aria-hidden="true" className="h-4 w-4" /> Add member</Button></Link>}
        </>}
      />
      <Card>
        {d.members.length === 0 ? <EmptyState title="No members yet" description="Add the household head first." /> : (
          <TableWrap>
            <thead><tr><Th>Name</Th><Th>Relationship</Th><Th>Age</Th><Th>Civil status</Th><Th>Occupation</Th><Th>Status</Th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {d.members.map((m) => (
                <tr key={m.id}>
                  <Td><Link className="font-medium hover:underline" to={`/residents/${m.id}`}>{m.displayName}</Link><div className="font-mono text-xs text-slate-500">{m.code}</div></Td>
                  <Td>{relLabel(m.relationship)}</Td>
                  <Td className="tabular-nums">{m.age} · {m.sex === 'MALE' ? 'M' : 'F'}</Td>
                  <Td>{CIVIL[m.civilStatus]}</Td>
                  <Td>{m.occupation || '—'}</Td>
                  <Td><StatusChip tone={STATUS[m.status].tone}>{STATUS[m.status].label}</StatusChip></Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>
      {editing && <HouseholdDialog initial={d} onClose={() => setEditing(false)} />}
    </div>
  );
}
